// HeroBlockEditor — big top-of-article media with title/subtitle overlay.
//
// Media can be:
//   • image — filename in media_assets
//   • video_embed — raw URL (YouTube/Vimeo)
//   • video_file — filename in media_assets
//
// Overlay position + theme tweak the title rendering.

import { useState } from "react";
import type { HeroBlock, Block } from "../../../lib/blocks";
import { fileUrl } from "../../../lib/pb";
import { MediaPicker } from "../MediaPicker";
import { t } from "../theme";

export interface HeroBlockEditorProps {
  block: HeroBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
  articleId: string;
  mediaAssets: string[];
  onUploadFile: (file: File) => Promise<string>;
}

const KINDS: HeroBlock["mediaKind"][] = ["image", "video_embed", "video_file"];
const POSITIONS: HeroBlock["overlayPosition"][] = [
  "bottom-left",
  "bottom-center",
  "center",
  "top-left",
];

export function HeroBlockEditor({
  block,
  onChange,
  isSelected,
  articleId,
  mediaAssets,
  onUploadFile,
}: HeroBlockEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [drag, setDrag] = useState(false);

  const isFile = block.mediaKind === "image" || block.mediaKind === "video_file";
  const overlayTheme = block.overlayTheme || "light";

  async function handleDropFile(file: File | null) {
    if (!file || !isFile) return;
    try {
      const filename = await onUploadFile(file);
      onChange({ media: filename } as Partial<HeroBlock>);
    } catch {
      /* swallow */
    }
  }

  return (
    <div>
      {isSelected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onChange({ mediaKind: k, media: "" } as Partial<HeroBlock>)}
              style={pillStyle(block.mediaKind === k)}
            >
              {k.replace("_", " ")}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["light", "dark"] as const).map((th) => (
            <button
              key={th}
              type="button"
              onClick={() => onChange({ overlayTheme: th } as Partial<HeroBlock>)}
              style={pillStyle(overlayTheme === th)}
            >
              {th}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {POSITIONS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onChange({ overlayPosition: p } as Partial<HeroBlock>)}
              style={pillStyle((block.overlayPosition || "bottom-left") === p)}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      <div
        onDragOver={(e) => {
          if (!isFile) return;
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          if (!isFile) return;
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files?.[0];
          void handleDropFile(f || null);
        }}
        style={{
          position: "relative",
          minHeight: 220,
          borderRadius: 8,
          overflow: "hidden",
          border: `2px dashed ${drag ? t.accent : block.media ? "transparent" : t.border}`,
          background: drag ? t.accentSoft : t.bg,
          display: "flex",
          alignItems: "stretch",
          color: t.text3,
        }}
      >
        {block.media ? (
          <HeroPreview block={block} articleId={articleId} />
        ) : (
          <div
            onClick={() => (isFile ? setPickerOpen(true) : null)}
            style={{
              flex: 1,
              padding: 32,
              textAlign: "center",
              fontFamily: t.sans,
              fontSize: 13,
              cursor: isFile ? "pointer" : "default",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ fontSize: 22, color: t.text4, marginBottom: 8 }}>★</div>
            {isFile ? "Drop hero media here or click to choose" : "Enter a video URL below"}
          </div>
        )}

        {/* Overlay title preview */}
        {block.media && (block.title || block.subtitle) && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              padding: 24,
              display: "flex",
              alignItems:
                (block.overlayPosition || "").startsWith("top") ? "flex-start" :
                (block.overlayPosition || "") === "center" ? "center" :
                "flex-end",
              justifyContent:
                (block.overlayPosition || "").includes("center") ? "center" : "flex-start",
              textAlign:
                (block.overlayPosition || "").includes("center") ? "center" : "left",
              background:
                overlayTheme === "light"
                  ? "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)"
                  : "linear-gradient(180deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.55) 100%)",
              color: overlayTheme === "light" ? "#fff" : "#1a1a1a",
              pointerEvents: "none",
            }}
          >
            <div>
              {block.title && (
                <div
                  style={{
                    fontFamily: t.serif,
                    fontSize: 30,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  {block.title}
                </div>
              )}
              {block.subtitle && (
                <div
                  style={{
                    marginTop: 6,
                    fontFamily: t.sans,
                    fontSize: 14,
                    opacity: 0.9,
                  }}
                >
                  {block.subtitle}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isSelected && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 10,
            fontFamily: t.sans,
          }}
        >
          {block.mediaKind === "video_embed" ? (
            <input
              type="url"
              value={block.media}
              onChange={(e) => onChange({ media: e.target.value } as Partial<HeroBlock>)}
              placeholder="https://youtube.com/… or https://vimeo.com/…"
              style={inputStyle}
            />
          ) : (
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" onClick={() => setPickerOpen(true)} style={ghostBtnStyle}>
                {block.media ? "Replace media…" : "Choose media…"}
              </button>
              {block.media && (
                <span style={{ fontSize: 11, color: t.text3, alignSelf: "center" }}>
                  {block.media}
                </span>
              )}
            </div>
          )}
          <input
            type="text"
            value={block.title || ""}
            onChange={(e) => onChange({ title: e.target.value } as Partial<HeroBlock>)}
            placeholder="Overlay title (optional)"
            style={inputStyle}
          />
          <input
            type="text"
            value={block.subtitle || ""}
            onChange={(e) => onChange({ subtitle: e.target.value } as Partial<HeroBlock>)}
            placeholder="Overlay subtitle (optional)"
            style={inputStyle}
          />
        </div>
      )}

      {pickerOpen && isFile && (
        <MediaPicker
          articleId={articleId}
          mediaAssets={mediaAssets}
          accept={block.mediaKind === "video_file" ? "video" : "image"}
          onPick={(filename) => {
            onChange({ media: filename } as Partial<HeroBlock>);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
          onUploadFile={onUploadFile}
        />
      )}
    </div>
  );
}

function HeroPreview({
  block,
  articleId,
}: {
  block: HeroBlock;
  articleId: string;
}) {
  if (block.mediaKind === "image") {
    return (
      <img
        src={fileUrl("articles", articleId, block.media, "1280x720")}
        alt=""
        style={{ width: "100%", height: 280, objectFit: "cover", display: "block" }}
      />
    );
  }
  if (block.mediaKind === "video_file") {
    return (
      <video
        src={fileUrl("articles", articleId, block.media)}
        controls
        style={{ width: "100%", height: 280, objectFit: "cover", background: "#000" }}
      />
    );
  }
  // video_embed — placeholder card; preview is handled visually in published view.
  return (
    <div
      style={{
        flex: 1,
        padding: 40,
        textAlign: "center",
        background: "#000",
        color: "#999",
        fontFamily: t.sans,
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 280,
      }}
    >
      ▶ {block.media}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  background: t.bg,
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 12,
  color: t.text,
  outline: "none",
  fontFamily: t.sans,
};

const ghostBtnStyle: React.CSSProperties = {
  padding: "5px 10px",
  background: "transparent",
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 11,
  color: t.text2,
  cursor: "pointer",
  fontFamily: t.sans,
};
